import { useState } from "react";
import type { SortSpec } from "@howcomet/shared";
import { CheckFrame } from "./CheckFrame";
import type { ShellProps } from "./registry";

/**
 * Tap-to-assign rather than drag-and-drop. Dragging is genuinely hard for small
 * hands on a touchscreen and fails silently when it misses; a tap either lands
 * or it doesn't.
 */
export function SortShell({ spec, onDone, collecting }: ShellProps<SortSpec>) {
  const [placed, setPlaced] = useState<Record<number, number>>({});
  const [misses, setMisses] = useState(0);

  const complete = Object.keys(placed).length === spec.items.length;
  const correct = complete && spec.items.every((item, index) => placed[index] === item.bucket);
  const outcome = complete ? (correct ? "right" : "wrong") : null;

  return (
    <CheckFrame
      spec={spec}
      outcome={outcome}
      onRetry={() => {
        setMisses((m) => m + 1);
        setPlaced({});
      }}
      onCollect={() => onDone({ correct: true, firstTry: misses === 0 })}
      collecting={collecting}
    >
      <div className="flex flex-col gap-3">
        {spec.items.map((item, index) => (
          <div
            key={item.label}
            className="flex flex-wrap items-center gap-2 rounded-2xl bg-panel p-3"
          >
            <span className="min-w-[150px] flex-1 text-[16px] font-bold text-cream">
              {item.label}
            </span>
            {spec.buckets.map((bucket, bucketIndex) => (
              <button
                key={bucket}
                disabled={complete}
                onClick={() => setPlaced((p) => ({ ...p, [index]: bucketIndex }))}
                aria-pressed={placed[index] === bucketIndex}
                className={`kidbtn rounded-full px-4 py-2 text-[15px] font-extrabold ${
                  placed[index] === bucketIndex ? "bg-cyan text-night" : "bg-cream/10 text-cream"
                }`}
              >
                {bucket}
              </button>
            ))}
          </div>
        ))}
      </div>
    </CheckFrame>
  );
}
